import { useState } from "react";

import { useT } from "../shared/i18n";
import {
  Badge,
  BottomSheet,
  IconButton,
  Icons,
  LargeTitle,
  TabBar,
  type TabBarItem,
} from "../shared/ui";
import type { SheetAction } from "../shared/ui";

/** Bottom padding of a top-level screen so content clears the 88px tab bar. */
export const TAB_BAR_CLEARANCE =
  "pb-[calc(env(safe-area-inset-bottom)+var(--tabbar-h))]";

export type TabId = "home" | "sections" | "profile";

/** The four top-level destinations; shown on top-level screens only. */
export function AppTabBar({
  active,
  onHome,
  onSections,
  onAssistant,
  onProfile,
}: {
  active: TabId;
  onHome: () => void;
  onSections: () => void;
  /** Same destination as "create your own": the chat. */
  onAssistant: () => void;
  onProfile: () => void;
}) {
  const t = useT();
  const items: TabBarItem[] = [
    {
      id: "home",
      label: t("nav.home"),
      icon: <Icons.Home size={24} />,
      active: active === "home",
      onSelect: onHome,
    },
    {
      id: "sections",
      label: t("nav.sections"),
      icon: <Icons.Grid size={24} />,
      active: active === "sections",
      onSelect: onSections,
    },
    {
      id: "assistant",
      label: t("nav.assistant"),
      icon: <Icons.Sparkles size={24} />,
      onSelect: onAssistant,
    },
    {
      id: "profile",
      label: t("nav.profile"),
      icon: <Icons.User size={24} />,
      active: active === "profile",
      onSelect: onProfile,
    },
  ];
  return <TabBar items={items} label={t("nav.aria")} />;
}

export interface MenuView {
  id: string;
  label: string;
  active: boolean;
  onSelect: () => void;
}

/**
 * Large title of a home/ability screen with the round section menu: edit
 * (own sections), the system marker, and the ability's other views.
 */
export function AbilityTitle({
  name,
  system,
  views,
  onEdit,
}: {
  name: string;
  system: boolean;
  views: MenuView[];
  /** Absent when the section cannot be edited here (system or local). */
  onEdit?: () => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const actions: SheetAction[] = [
    ...(onEdit
      ? [{ id: "edit", label: t("sections.edit"), onSelect: onEdit }]
      : []),
    ...views.map((view) => ({
      id: view.id,
      label: view.label,
      active: view.active,
      onSelect: view.onSelect,
    })),
  ];
  return (
    <>
      <LargeTitle
        title={name}
        eyebrow={
          system ? <Badge tone="system">{t("nav.system")}</Badge> : undefined
        }
        className="px-gutter pb-3 pt-4"
      >
        {actions.length > 0 && (
          <IconButton
            label={t("nav.sectionMenu")}
            variant="secondary"
            className="size-10 shrink-0 rounded-full"
            onClick={() => {
              setOpen(true);
            }}
          >
            <Icons.More size={20} />
          </IconButton>
        )}
      </LargeTitle>
      {actions.length > 0 && (
        <BottomSheet
          open={open}
          onOpenChange={setOpen}
          title={t("nav.sectionMenu")}
          actions={actions}
        />
      )}
    </>
  );
}
